import { useState, useEffect, useRef } from "react";

// ── Design Tokens ──
const gold = "#f5a623";
const blue = "#3b82f6", blue2 = "#60a5fa";
const green = "#22c55e";
const red = "#ef4444";
const purple = "#a78bfa";
const navy = "#0b0f1a", navy2 = "#111827", navy3 = "#1a2236", navy4 = "#212d45";
const white = "#ffffff", textCol = "#cbd5e1", muted = "#64748b";
const border = "rgba(255,255,255,0.07)";

// ── Scroll Reveal ──
function useReveal() {
  const ref = useRef(null);
  const [vis, setVis] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setVis(true); }, { threshold: 0.08 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  return [ref, vis];
}
function Reveal({ children, delay = 0 }) {
  const [ref, vis] = useReveal();
  return (
    <div ref={ref} style={{ opacity: vis ? 1 : 0, transform: vis ? "translateY(0)" : "translateY(22px)", transition: `opacity 0.6s ease ${delay}s, transform 0.6s ease ${delay}s` }}>
      {children}
    </div>
  );
}

// ── Atoms ──
const s = {
  section: { marginBottom: 72 },
  eyebrow: { display: "flex", alignItems: "center", gap: 12, marginBottom: 12 },
  tag: { fontSize: "0.68rem", fontWeight: 700, letterSpacing: "0.16em", textTransform: "uppercase", color: blue },
  line: { flex: 1, height: 1, background: border },
  badge: { fontFamily: "monospace", fontSize: "0.68rem", color: muted },
  h2: { fontFamily: "Georgia,serif", fontSize: "clamp(1.5rem,3vw,2.1rem)", fontWeight: 900, color: white, lineHeight: 1.25, marginBottom: 20 },
  prose: { fontSize: "0.97rem", color: textCol, lineHeight: 1.85, marginBottom: 28 },
  hr: { border: "none", borderTop: `1px solid ${border}`, margin: "48px 0" },
};

function Callout({ icon, children, accent = gold }) {
  return (
    <div style={{ background: navy3, border: `1px solid ${border}`, borderLeft: `3px solid ${accent}`, borderRadius: 8, padding: "20px 24px", margin: "28px 0", display: "flex", gap: 14, alignItems: "flex-start" }}>
      <span style={{ fontSize: "1.1rem", flexShrink: 0, marginTop: 2 }}>{icon}</span>
      <div style={{ fontSize: "0.88rem", color: textCol, lineHeight: 1.75 }}>{children}</div>
    </div>
  );
}

function Pill({ text, color = gold }) {
  return <span style={{ display: "inline-block", fontSize: "0.75rem", fontWeight: 700, padding: "3px 10px", borderRadius: 4, color, background: `${color}18`, border: `1px solid ${color}33`, marginRight: 4, marginBottom: 4 }}>{text}</span>;
}

function FeatCard({ icon, title, body }) {
  const [hov, setHov] = useState(false);
  return (
    <div onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}
      style={{ background: navy3, border: `1px solid ${hov ? "rgba(59,130,246,.35)" : border}`, borderRadius: 8, padding: "22px 20px", transition: "all 0.2s", transform: hov ? "translateY(-2px)" : "none" }}>
      <div style={{ fontSize: "1.4rem", marginBottom: 10 }}>{icon}</div>
      <div style={{ fontSize: "0.85rem", fontWeight: 700, color: white, marginBottom: 6 }}>{title}</div>
      <div style={{ fontSize: "0.82rem", color: muted, lineHeight: 1.65 }}>{body}</div>
    </div>
  );
}

// ── Section 1: Income Type Comparison ──
const incomeTypes = [
  { id: "interest", label: "💵 Interest", name: "Interest Income", color: red, inclusion: "100%", sources: "GICs, bonds, T-bills, foreign dividends", desc: "The most heavily taxed form. Every dollar earned is added directly to taxable income at your full marginal rate.", tag: "Most Taxed" },
  { id: "dividends", label: "📊 Dividends", name: "Canadian Dividends", color: gold, inclusion: "Gross-Up 38%", sources: "Shares of taxable Canadian corporations", desc: "Grossed-up by 38%, then taxed at the marginal rate — but a federal dividend tax credit (15.02% of grossed-up amount) partially offsets the tax.", tag: "Preferred Rate" },
  { id: "gains", label: "📈 Capital Gains", name: "Capital Gains", color: green, inclusion: "50%", sources: "Stocks, mutual funds, real estate sold at a profit", desc: "Only half (50%) of the gain is included in taxable income. The most tax-efficient form of investment income.", tag: "Most Tax-Efficient" },
];

function IncomeTypeTabs() {
  const [active, setActive] = useState(0);
  const it = incomeTypes[active];
  return (
    <div style={{ margin: "28px 0" }}>
      <div style={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
        {incomeTypes.map((t, i) => (
          <button key={i} onClick={() => setActive(i)} style={{ padding: "10px 18px", background: active === i ? navy4 : navy3, border: `1px solid ${active === i ? `${t.color}44` : border}`, borderBottom: active === i ? `2px solid ${t.color}` : `1px solid ${border}`, borderRadius: "6px 6px 0 0", cursor: "pointer", fontFamily: "inherit", fontSize: "0.78rem", fontWeight: 700, color: active === i ? t.color : muted, transition: "all 0.2s", whiteSpace: "nowrap" }}>{t.label}</button>
        ))}
      </div>
      <div style={{ background: navy4, border: `1px solid ${it.color}22`, borderRadius: "0 0 8px 8px", padding: 26 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
          <div style={{ fontFamily: "Georgia,serif", fontSize: "1.1rem", color: white }}>{it.name}</div>
          <span style={{ fontSize: "0.65rem", fontWeight: 700, padding: "3px 10px", borderRadius: 100, color: it.color, background: `${it.color}18`, border: `1px solid ${it.color}33`, letterSpacing: "0.1em", textTransform: "uppercase" }}>{it.tag}</span>
        </div>
        <p style={{ fontSize: "0.9rem", color: textCol, lineHeight: 1.8, marginBottom: 14 }}>{it.desc}</p>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          {[["Taxable Inclusion", it.inclusion], ["Sources", it.sources]].map(([lbl, val], j) => (
            <div key={j} style={{ background: navy3, borderRadius: 6, padding: "12px 14px" }}>
              <div style={{ fontSize: "0.65rem", fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: muted, marginBottom: 4 }}>{lbl}</div>
              <div style={{ fontSize: "0.85rem", color: textCol }}>{val}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── Tax Calculator ──
function TaxCalculator() {
  const [income, setIncome] = useState(1000);
  const [rate, setRate] = useState(40);
  const [result, setResult] = useState(null);

  function calc() {
    const inc = parseFloat(income) || 0;
    const r = parseFloat(rate) / 100;

    // Interest
    const interestTax = inc * r;

    // Capital Gains
    const cgTaxable = inc * 0.5;
    const cgTax = cgTaxable * r;

    // Dividends
    const grossedUp = inc * 1.38;
    const grossTax = grossedUp * r;
    const dtc = grossedUp * 0.1502;
    const divTax = grossTax - dtc;

    setResult({ inc, r: rate, interestTax, cgTaxable, cgTax, grossedUp, grossTax, dtc, divTax });
  }

  const barMax = result ? Math.max(result.interestTax, result.divTax, result.cgTax) : 1;

  return (
    <div style={{ background: navy3, border: `1px solid ${border}`, borderRadius: 8, padding: 28, margin: "28px 0" }}>
      <div style={{ fontFamily: "Georgia,serif", fontSize: "1rem", color: white, marginBottom: 6 }}>🧮 Investment Income Tax Comparison</div>
      <p style={{ fontSize: "0.85rem", color: muted, marginBottom: 20, lineHeight: 1.65 }}>Enter an income amount and your marginal tax rate to see the after-tax cost of each income type side-by-side.</p>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 16 }}>
        {[["Income Amount ($)", income, setIncome, 100, 100000], ["Marginal Tax Rate (%)", rate, setRate, 20, 54]].map(([lbl, val, set, min, max], i) => (
          <div key={i}>
            <label style={{ display: "block", fontSize: "0.72rem", fontWeight: 700, color: muted, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 6 }}>{lbl}</label>
            <input type="number" value={val} onChange={e => set(e.target.value)} min={min} max={max}
              style={{ width: "100%", background: navy4, border: `1px solid ${border}`, borderRadius: 6, padding: "11px 13px", color: white, fontFamily: "monospace", fontSize: "0.92rem", outline: "none" }} />
          </div>
        ))}
      </div>
      <button onClick={calc} style={{ background: gold, color: navy, border: "none", padding: "12px 28px", fontFamily: "inherit", fontSize: "0.85rem", fontWeight: 700, borderRadius: 100, cursor: "pointer", marginBottom: result ? 20 : 0 }}>Compare Tax Impact →</button>

      {result && (
        <>
          {/* Bar chart comparison */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10, marginBottom: 20 }}>
            {[
              { label: "Interest Income", tax: result.interestTax, color: red, tag: "100% Taxable" },
              { label: "Canadian Dividends", tax: result.divTax, color: gold, tag: "Gross-Up + DTC" },
              { label: "Capital Gains", tax: result.cgTax, color: green, tag: "50% Inclusion" },
            ].map((item, i) => (
              <div key={i} style={{ background: navy4, borderRadius: 8, padding: "16px 14px", textAlign: "center" }}>
                <div style={{ fontSize: "0.65rem", fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: item.color, marginBottom: 8 }}>{item.label}</div>
                <div style={{ height: 80, display: "flex", alignItems: "flex-end", justifyContent: "center", marginBottom: 8 }}>
                  <div style={{ width: 36, background: `${item.color}22`, border: `1px solid ${item.color}44`, borderRadius: "4px 4px 0 0", height: `${Math.round((item.tax / barMax) * 80)}px`, transition: "height 0.5s ease", display: "flex", alignItems: "flex-start", justifyContent: "center", paddingTop: 4 }}>
                    <div style={{ width: 28, height: 4, borderRadius: 2, background: item.color }} />
                  </div>
                </div>
                <div style={{ fontFamily: "monospace", fontSize: "1.1rem", color: item.color, fontWeight: 700 }}>${item.tax.toLocaleString("en-CA", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                <div style={{ fontSize: "0.7rem", color: muted, marginTop: 4 }}>{item.tag}</div>
                <div style={{ fontSize: "0.75rem", color: textCol, marginTop: 4 }}>After-tax: ${(result.inc - item.tax).toLocaleString("en-CA", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
              </div>
            ))}
          </div>

          {/* Step-by-step workings */}
          <div style={{ display: "grid", gap: 10 }}>
            {[
              { label: "Interest Income Calculation", color: red, steps: [`Taxable amount: $${result.inc.toLocaleString()} × 100% = $${result.inc.toLocaleString()}`, `Tax: $${result.inc.toLocaleString()} × ${result.r}% = $${result.interestTax.toLocaleString("en-CA", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`] },
              { label: "Capital Gains Calculation", color: green, steps: [`Taxable portion: $${result.inc.toLocaleString()} × 50% = $${result.cgTaxable.toLocaleString()}`, `Tax: $${result.cgTaxable.toLocaleString()} × ${result.r}% = $${result.cgTax.toLocaleString("en-CA", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`] },
              { label: "Canadian Dividend Calculation", color: gold, steps: [`Step 1 — Gross-Up: $${result.inc.toLocaleString()} × 1.38 = $${result.grossedUp.toLocaleString("en-CA", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, `Step 2 — Gross Tax: $${result.grossedUp.toLocaleString("en-CA", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} × ${result.r}% = $${result.grossTax.toLocaleString("en-CA", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, `Step 3 — DTC: $${result.grossedUp.toLocaleString("en-CA", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} × 15.02% = $${result.dtc.toLocaleString("en-CA", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, `Step 4 — Net Tax: $${result.grossTax.toLocaleString("en-CA", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} − $${result.dtc.toLocaleString("en-CA", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} = $${result.divTax.toLocaleString("en-CA", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`] },
            ].map((item, i) => (
              <div key={i} style={{ background: navy4, borderRadius: 6, padding: "14px 16px", borderLeft: `3px solid ${item.color}` }}>
                <div style={{ fontSize: "0.68rem", fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", color: item.color, marginBottom: 8 }}>{item.label}</div>
                {item.steps.map((step, j) => (
                  <div key={j} style={{ fontFamily: "monospace", fontSize: "0.8rem", color: textCol, padding: "3px 0", display: "flex", gap: 8 }}>
                    <span style={{ color: item.color, flexShrink: 0 }}>→</span>{step}
                  </div>
                ))}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// ── Section 2: Government Pensions ──
function PensionCards() {
  const [active, setActive] = useState(0);
  const plans = [
    {
      label: "🍁 CPP / QPP", name: "Canada Pension Plan", sub: "Quebec Pension Plan",
      color: blue2, accent: blue,
      rows: [
        ["Eligibility", "Every employed Canadian who has contributed; QPP for Quebec workers"],
        ["Standard Age", "65"],
        ["Early Takeup", "As early as age 60 — benefit permanently reduced by 0.6% per month before 65"],
        ["Late Deferral", "Up to age 70 — benefit permanently increased by 0.7% per month after 65"],
        ["Taxation", "Benefits are fully taxable as income"],
        ["Funded By", "Mandatory employee and employer contributions (matching)"],
      ],
    },
    {
      label: "🏛️ OAS", name: "Old Age Security",
      color: gold, accent: gold,
      rows: [
        ["Eligibility", "Based on Canadian residency — not employment history"],
        ["Full Pension", "Requires 40 years of residency after age 18"],
        ["Minimum", "At least 10 years of residency required to collect anything"],
        ["Standard Age", "65 (can defer to 70 for an increased benefit)"],
        ["Clawback", "If net income exceeds ~$90,997 (2024), 15% of the excess is repaid to the government"],
        ["Taxation", "Benefits are fully taxable; subject to clawback at higher incomes"],
      ],
    },
  ];
  const p = plans[active];
  return (
    <div style={{ margin: "28px 0" }}>
      <div style={{ display: "flex", gap: 2, marginBottom: 0 }}>
        {plans.map((pl, i) => (
          <button key={i} onClick={() => setActive(i)} style={{ padding: "10px 20px", background: active === i ? navy4 : navy3, border: `1px solid ${active === i ? `${pl.color}44` : border}`, borderBottom: active === i ? `2px solid ${pl.accent}` : `1px solid ${border}`, borderRadius: "6px 6px 0 0", cursor: "pointer", fontFamily: "inherit", fontSize: "0.78rem", fontWeight: 700, color: active === i ? pl.color : muted, transition: "all 0.2s" }}>{pl.label}</button>
        ))}
      </div>
      <div style={{ background: navy4, border: `1px solid ${p.accent}22`, borderRadius: "0 0 8px 8px", padding: 26 }}>
        <div style={{ fontFamily: "Georgia,serif", fontSize: "1.15rem", color: white, marginBottom: p.sub ? 4 : 14 }}>{p.name}</div>
        {p.sub && <div style={{ fontSize: "0.8rem", color: muted, marginBottom: 16 }}>{p.sub}</div>}
        <div style={{ display: "grid", gap: 8 }}>
          {p.rows.map(([lbl, val], i) => (
            <div key={i} style={{ background: navy3, borderRadius: 6, padding: "11px 14px", display: "flex", gap: 12, flexWrap: "wrap" }}>
              <div style={{ fontSize: "0.68rem", fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: p.color, width: 110, flexShrink: 0, paddingTop: 2 }}>{lbl}</div>
              <div style={{ fontSize: "0.85rem", color: textCol, flex: 1, lineHeight: 1.6 }}>{val}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── Section 3: RPP Plans ──
function RPPComparison() {
  const [active, setActive] = useState(null);
  const plans = [
    {
      id: "dbp", icon: "🏆", label: "Defined Benefit Plan", badge: "DBP", color: gold,
      tagline: "Benefit is known. Contribution varies.",
      body: "The pension amount is defined in advance — the employer bears the investment risk and must fund whatever is needed to deliver that promised benefit.",
      subtypes: [
        { name: "Flat Benefit", desc: "Fixed dollar amount per month for each year of service.", example: "$50/month × 30 years = $1,500/month. Common in unionized environments." },
        { name: "Career Average", desc: "Based on average earnings over the entire career.", example: "Lower payout early, higher if salaries rise significantly late in career." },
        { name: "Final Average", desc: "Based on average earnings of the best years (e.g., best 3 of last 5).", example: "Best protection against inflation — captures peak earning years." },
      ],
    },
    {
      id: "dcp", icon: "📦", label: "Defined Contribution Plan", badge: "DCP", color: blue2,
      tagline: "Contribution is known. Benefit varies.",
      body: "Also called a Money Purchase Plan. Contributions are fixed; the final benefit depends entirely on investment performance. The employee bears the market risk.",
      subtypes: [
        { name: "Contribution Limit", desc: "Combined employer + employee contributions cannot exceed 18% of compensation.", example: "Subject to an annual CRA dollar cap (same as RRSP limit)." },
        { name: "Investment Risk", desc: "The employee decides how to invest, and the retirement income is unknown until retirement.", example: "A DCP member who retires in a market downturn may receive far less than expected." },
      ],
    },
  ];

  return (
    <div style={{ margin: "28px 0" }}>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
        {plans.map((plan, i) => (
          <div key={i} onClick={() => setActive(active === i ? null : i)}
            style={{ background: active === i ? navy4 : navy3, border: `1px solid ${active === i ? `${plan.color}55` : border}`, borderRadius: 8, padding: 22, cursor: "pointer", transition: "all 0.2s", position: "relative", overflow: "hidden" }}>
            <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 3, background: `linear-gradient(90deg,${plan.color},transparent)` }} />
            <div style={{ fontSize: "1.6rem", marginBottom: 8 }}>{plan.icon}</div>
            <div style={{ fontSize: "0.65rem", fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", color: plan.color, marginBottom: 4 }}>{plan.badge}</div>
            <div style={{ fontFamily: "Georgia,serif", fontSize: "1rem", color: white, marginBottom: 6 }}>{plan.label}</div>
            <div style={{ fontSize: "0.82rem", color: plan.color, fontWeight: 700, marginBottom: 8, fontStyle: "italic" }}>{plan.tagline}</div>
            <div style={{ fontSize: "0.82rem", color: muted, lineHeight: 1.65 }}>{plan.body}</div>
            <div style={{ fontSize: "0.72rem", color: active === i ? plan.color : muted, marginTop: 12 }}>{active === i ? "↑ Click to collapse" : "↓ Click for subtypes"}</div>
          </div>
        ))}
      </div>
      {active !== null && (
        <div style={{ background: navy4, border: `1px solid ${plans[active].color}33`, borderRadius: 8, padding: 22 }}>
          <div style={{ fontSize: "0.68rem", fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", color: plans[active].color, marginBottom: 14 }}>{plans[active].label} — Subtypes</div>
          <div style={{ display: "grid", gap: 10 }}>
            {plans[active].subtypes.map((st, j) => (
              <div key={j} style={{ background: navy3, borderRadius: 6, padding: "14px 16px" }}>
                <div style={{ fontSize: "0.82rem", fontWeight: 700, color: white, marginBottom: 4 }}>{st.name}</div>
                <div style={{ fontSize: "0.82rem", color: textCol, lineHeight: 1.65, marginBottom: 6 }}>{st.desc}</div>
                <div style={{ fontSize: "0.8rem", color: muted, fontStyle: "italic" }}>{st.example}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Section 4: Tax Deferral Plans ──
const plans = [
  {
    id: "rrsp", icon: "🏦", label: "RRSP", name: "Registered Retirement Savings Plan",
    color: gold, badge: "Tax-Deferred",
    keyFact: "Contributions reduce taxable income today; growth and withdrawals taxed later.",
    rows: [
      ["Contribution", "Tax-deductible — reduces your taxable income in the year contributed"],
      ["Growth", "Tax-sheltered inside the plan until withdrawal"],
      ["Withdrawal", "Fully taxable as income in the year withdrawn"],
      ["Deadline", "Must be collapsed by end of the year you turn 71"],
      ["Limit Formula", "18% of previous year earned income − Pension Adjustment + unused room"],
      ["Spousal RRSP", "Contributor gets deduction; withdrawals taxed in spouse's hands (income splitting)"],
    ],
    pills: ["Tax Deductible", "Tax-Sheltered Growth", "Income Split (Spousal)"],
  },
  {
    id: "rrif", icon: "🔄", label: "RRIF", name: "Registered Retirement Income Fund",
    color: blue2, badge: "RRSP Successor",
    keyFact: "Growth stays tax-sheltered, but mandatory minimum withdrawals apply each year.",
    rows: [
      ["Purpose", "Convert your RRSP into a retirement income stream"],
      ["Deadline", "RRSP must be converted to a RRIF by end of the year you turn 71"],
      ["Growth", "Investments continue to grow tax-sheltered within the RRIF"],
      ["Withdrawals", "Minimum annual withdrawal is mandatory (% increases with age)"],
      ["Taxation", "Withdrawals are fully taxable as income in the year received"],
    ],
    pills: ["RRSP Successor", "Mandatory Minimums", "Tax-Sheltered Growth"],
  },
  {
    id: "tfsa", icon: "🆓", label: "TFSA", name: "Tax-Free Savings Account",
    color: green, badge: "Tax-Free",
    keyFact: "Contributions are NOT deductible, but all growth and withdrawals are 100% tax-free.",
    rows: [
      ["Contributions", "Made with after-tax dollars — no deduction"],
      ["Growth", "Completely tax-free inside the account"],
      ["Withdrawals", "Completely tax-free at any time, for any reason"],
      ["Carry-Forward", "Unused room accumulates indefinitely and is restored when you withdraw"],
      ["Over-Contribution", "Penalty tax of 1% per month on the excess amount"],
      ["Best For", "Tax-free compounding; income splitting for high-income earners; supplements RRSP"],
    ],
    pills: ["Tax-Free Growth", "Tax-Free Withdrawals", "Room Restored on Withdrawal"],
  },
  {
    id: "resp", icon: "🎓", label: "RESP", name: "Registered Education Savings Plan",
    color: purple, badge: "Education",
    keyFact: "Government adds 20% (CESG) on first $2,500/year. Growth taxed in the student's hands.",
    rows: [
      ["Contributions", "Not tax-deductible — made with after-tax dollars"],
      ["CESG Grant", "Government matches 20% of first $2,500/year = max $500/year per child"],
      ["Lifetime CESG", "Maximum $7,200 in CESG grants per beneficiary"],
      ["Growth", "Tax-sheltered until withdrawn for education"],
      ["EAP Taxation", "Educational Assistance Payments taxed in student's hands (usually minimal tax)"],
      ["Return of Contributions", "Your original contributions come back to you tax-free"],
    ],
    pills: ["20% CESG Grant", "Taxed in Student's Hands", "Lifetime $7,200 Grant Max"],
  },
];

function TaxPlanTabs() {
  const [active, setActive] = useState(0);
  const p = plans[active];
  return (
    <div style={{ margin: "28px 0" }}>
      <div style={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
        {plans.map((pl, i) => (
          <button key={i} onClick={() => setActive(i)} style={{ padding: "10px 18px", background: active === i ? navy4 : navy3, border: `1px solid ${active === i ? `${pl.color}44` : border}`, borderBottom: active === i ? `2px solid ${pl.color}` : `1px solid ${border}`, borderRadius: "6px 6px 0 0", cursor: "pointer", fontFamily: "inherit", fontSize: "0.78rem", fontWeight: 700, color: active === i ? pl.color : muted, transition: "all 0.2s", whiteSpace: "nowrap" }}>
            {pl.icon} {pl.label}
          </button>
        ))}
      </div>
      <div style={{ background: navy4, border: `1px solid ${p.color}22`, borderRadius: "0 0 8px 8px", padding: 26 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
          <div style={{ fontFamily: "Georgia,serif", fontSize: "1.15rem", color: white }}>{p.name}</div>
          <span style={{ fontSize: "0.65rem", fontWeight: 700, padding: "3px 10px", borderRadius: 100, color: p.color, background: `${p.color}18`, border: `1px solid ${p.color}33`, letterSpacing: "0.1em", textTransform: "uppercase" }}>{p.badge}</span>
        </div>
        <div style={{ background: navy3, borderLeft: `3px solid ${p.color}`, borderRadius: 6, padding: "12px 16px", marginBottom: 16, fontSize: "0.88rem", color: textCol, fontStyle: "italic", lineHeight: 1.65 }}>💡 {p.keyFact}</div>
        <div style={{ display: "grid", gap: 8 }}>
          {p.rows.map(([lbl, val], i) => (
            <div key={i} style={{ background: navy3, borderRadius: 6, padding: "11px 14px", display: "flex", gap: 12, flexWrap: "wrap" }}>
              <div style={{ fontSize: "0.68rem", fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: p.color, width: 120, flexShrink: 0, paddingTop: 2 }}>{lbl}</div>
              <div style={{ fontSize: "0.85rem", color: textCol, flex: 1, lineHeight: 1.6 }}>{val}</div>
            </div>
          ))}
        </div>
        <div style={{ marginTop: 14 }}>
          {p.pills.map((pl, i) => <Pill key={i} text={pl} color={p.color} />)}
        </div>
      </div>
    </div>
  );
}

// ── RRSP Calculator ──
function RRSPCalc() {
  const [earnedIncome, setEarnedIncome] = useState(60000);
  const [pa, setPa] = useState(3000);
  const [carryForward, setCarryForward] = useState(5000);
  const [annualCap] = useState(31560);
  const [result, setResult] = useState(null);

  function calc() {
    const ei = parseFloat(earnedIncome) || 0;
    const paVal = parseFloat(pa) || 0;
    const cf = parseFloat(carryForward) || 0;
    const eighteenPct = ei * 0.18;
    const beforeCap = Math.min(eighteenPct, annualCap);
    const afterPA = beforeCap - paVal;
    const total = afterPA + cf;
    setResult({ ei, eighteenPct, beforeCap, paVal, afterPA, cf, total, annualCap });
  }

  return (
    <div style={{ background: navy3, border: `1px solid ${border}`, borderRadius: 8, padding: 28, margin: "28px 0" }}>
      <div style={{ fontFamily: "Georgia,serif", fontSize: "1rem", color: white, marginBottom: 6 }}>🧮 RRSP Contribution Room Calculator</div>
      <div style={{ fontFamily: "monospace", fontSize: "0.78rem", color: gold, background: "rgba(245,166,35,.12)", border: "1px solid rgba(245,166,35,.2)", padding: "8px 14px", borderRadius: 4, display: "inline-block", marginBottom: 20, lineHeight: 1.7 }}>
        Room = (Prior Year Earned Income × 18%) − Pension Adjustment + Unused Carry-Forward
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(180px,1fr))", gap: 10, marginBottom: 16 }}>
        {[["Prior Year Earned Income ($)", earnedIncome, setEarnedIncome], ["Pension Adjustment — PA ($)", pa, setPa], ["Unused Carry-Forward ($)", carryForward, setCarryForward]].map(([lbl, val, set], i) => (
          <div key={i}>
            <label style={{ display: "block", fontSize: "0.72rem", fontWeight: 700, color: muted, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 6 }}>{lbl}</label>
            <input type="number" value={val} onChange={e => set(e.target.value)}
              style={{ width: "100%", background: navy4, border: `1px solid ${border}`, borderRadius: 6, padding: "11px 13px", color: white, fontFamily: "monospace", fontSize: "0.92rem", outline: "none" }} />
          </div>
        ))}
      </div>
      <button onClick={calc} style={{ background: gold, color: navy, border: "none", padding: "12px 28px", fontFamily: "inherit", fontSize: "0.85rem", fontWeight: 700, borderRadius: 100, cursor: "pointer" }}>Calculate RRSP Room →</button>

      {result && (
        <div style={{ background: navy4, border: "1px solid rgba(245,166,35,.2)", borderRadius: 8, padding: 20, marginTop: 16 }}>
          <div style={{ fontSize: "0.68rem", fontWeight: 700, letterSpacing: "0.14em", textTransform: "uppercase", color: gold, marginBottom: 6 }}>RRSP Contribution Room</div>
          <div style={{ fontFamily: "Georgia,serif", fontSize: "2rem", fontWeight: 900, color: white }}>${result.total.toLocaleString("en-CA", { maximumFractionDigits: 0 })}</div>
          <ul style={{ listStyle: "none", padding: 0, margin: "14px 0 0", borderTop: `1px solid ${border}`, paddingTop: 12 }}>
            {[
              `18% of earned income: $${result.ei.toLocaleString()} × 18% = $${result.eighteenPct.toLocaleString()}`,
              `Compare to annual cap ($${result.annualCap.toLocaleString()}): use $${result.beforeCap.toLocaleString()}`,
              `Subtract PA: $${result.beforeCap.toLocaleString()} − $${result.paVal.toLocaleString()} = $${result.afterPA.toLocaleString()}`,
              `Add carry-forward: $${result.afterPA.toLocaleString()} + $${result.cf.toLocaleString()} = $${result.total.toLocaleString()}`,
            ].map((step, i) => (
              <li key={i} style={{ fontFamily: "monospace", fontSize: "0.79rem", color: textCol, padding: "4px 0", display: "flex", gap: 8 }}>
                <span style={{ color: gold, flexShrink: 0 }}>→</span>{step}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

// ── Quiz ──
const questions = [
  { q: "Which type of investment income generally attracts the highest amount of tax?", opts: ["Capital Gains", "Canadian Dividends", "Interest Income", "Foreign Dividends (with tax credit)"], correct: 2, fb: { y: "Correct! Interest income is 100% taxable at your full marginal rate — every dollar is included in taxable income. It is the most heavily taxed investment income.", n: "Not quite. Interest income is 100% included in taxable income, making it the most heavily taxed. Capital gains (50% inclusion) are most tax-efficient." } },
  { q: "What is the 'Gross-Up' percentage applied to eligible Canadian dividends?", opts: ["50%", "25%", "15.02%", "38%"], correct: 3, fb: { y: "Correct! Eligible Canadian dividends are grossed-up by 38% to arrive at the 'taxable amount.' The 15.02% figure is the federal dividend tax credit rate, not the gross-up.", n: "Not quite. The gross-up rate is 38% — the dividend is multiplied by 1.38. The 15.02% is the federal dividend tax credit applied afterward." } },
  { q: "If an individual sells a stock for a profit of $5,000, how much is added to their taxable income?", opts: ["$5,000", "$2,500", "$3,800", "$0 if held longer than a year"], correct: 1, fb: { y: "Correct! Capital gains have a 50% inclusion rate. Only half ($2,500) of the $5,000 gain is included in taxable income.", n: "Not quite. Canada's 50% inclusion rate means only half the capital gain ($2,500 of $5,000) is added to taxable income." } },
  { q: "Which pension plan type is based on the 'best years' of a member's earnings?", opts: ["Career Average Plan", "Flat Benefit Plan", "Final Average Plan", "Defined Contribution Plan"], correct: 2, fb: { y: "Correct! The Final Average Plan uses the average of the best earnings years (e.g., best 3 of last 5). It provides the best inflation protection.", n: "Not quite. The Final Average Plan uses peak earnings years. Career Average uses the whole career. Flat Benefit is a fixed dollar amount per year of service." } },
  { q: "By what age must an RRSP be collapsed or converted to a maturity option like a RRIF?", opts: ["65", "69", "71", "75"], correct: 2, fb: { y: "Correct! An RRSP must be collapsed, converted to a RRIF, or used to purchase an annuity by the end of the calendar year in which the holder turns 71.", n: "Not quite. The RRSP deadline is the end of the year you turn 71 — not 65 or 69. After that it must become a RRIF or annuity." } },
  { q: "What is the maximum CESG grant that can be earned in a single year for a child (no carry-forward)?", opts: ["$400", "$500", "$1,000", "$2,000"], correct: 1, fb: { y: "Correct! The CESG is 20% of the first $2,500 contributed annually. 20% × $2,500 = $500 maximum per year per child.", n: "Not quite. CESG = 20% × first $2,500 = $500/year max. You'd need to contribute $2,500 to earn the full $500 grant." } },
  { q: "If a client over-contributes to their TFSA, what is the penalty tax per month on the excess amount?", opts: ["1%", "5%", "10%", "25%"], correct: 0, fb: { y: "Correct! The TFSA over-contribution penalty is 1% per month on the excess amount until the excess is withdrawn.", n: "Not quite. The TFSA over-contribution penalty is 1% per month. This can compound quickly if not corrected — always verify contribution room before contributing." } },
  { q: "Which of the following reduces the amount of RRSP contribution room earned in a year?", opts: ["CPP contributions", "The Pension Adjustment (PA)", "Dividend Tax Credits", "OAS Clawback"], correct: 1, fb: { y: "Correct! The Pension Adjustment (PA) reduces RRSP room to account for the value of benefits accruing in an employer pension plan — preventing double-dipping on tax-sheltered savings.", n: "Not quite. The Pension Adjustment (PA) reduces RRSP room. It represents the value of RPP benefits earned that year, preventing taxpayers from sheltering the same income twice." } },
  { q: "Which statement about the Canada Pension Plan (CPP) is TRUE?", opts: ["You must be fully retired to collect it", "Benefits are tax-free", "You can apply as early as age 60 with a permanently reduced benefit", "Every Canadian citizen receives it regardless of work history"], correct: 2, fb: { y: "Correct! CPP can be taken as early as age 60, but the benefit is permanently reduced. Conversely, deferring past 65 (up to age 70) permanently increases it.", n: "Not quite. CPP can be taken from age 60 (reduced) to 70 (enhanced). It is based on contributions, is fully taxable, and does not require full retirement." } },
  { q: "What is the primary benefit of a Spousal RRSP?", opts: ["It doubles the contribution limit for the family", "It allows income splitting in retirement to lower the family's total tax bill", "It eliminates the Pension Adjustment", "It allows the spouse to withdraw funds tax-free immediately"], correct: 1, fb: { y: "Correct! A Spousal RRSP allows a high-income earner to contribute (and claim the deduction) while future withdrawals are taxed in the lower-income spouse's hands — splitting income and reducing the family's total tax.", n: "Not quite. The Spousal RRSP's purpose is income splitting in retirement — the contributor gets the deduction now; the spouse pays tax on withdrawals later (at their likely lower rate)." } },
];

function Quiz() {
  const [current, setCurrent] = useState(0);
  const [answered, setAnswered] = useState({});
  const [done, setDone] = useState(false);
  const score = Object.values(answered).filter(a => a.correct).length;
  const total = questions.length;

  function answer(idx) {
    if (answered[current] !== undefined) return;
    setAnswered({ ...answered, [current]: { chosen: idx, correct: idx === questions[current].correct } });
  }
  function next() { if (current < total - 1) setCurrent(current + 1); else setDone(true); }
  function retry() { setCurrent(0); setAnswered({}); setDone(false); }

  const q = questions[current];
  const ans = answered[current];
  const pct = Math.round(score / total * 100);

  return (
    <div style={{ background: navy2, border: `1px solid ${border}`, borderRadius: 12, padding: 36, marginTop: 28 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 28, flexWrap: "wrap", gap: 12 }}>
        <div style={{ fontFamily: "Georgia,serif", fontSize: "1.3rem", color: white }}>Chapter 6 Quiz</div>
        <div style={{ fontFamily: "monospace", fontSize: "0.78rem", color: muted }}>Question {current + 1} of {total}</div>
      </div>
      <div style={{ height: 4, background: navy4, borderRadius: 2, marginBottom: 28, overflow: "hidden" }}>
        <div style={{ height: "100%", background: `linear-gradient(90deg,${gold},${blue})`, borderRadius: 2, width: `${(current + 1) / total * 100}%`, transition: "width 0.4s ease" }} />
      </div>
      {!done ? (
        <>
          <div style={{ fontFamily: "Georgia,serif", fontSize: "1.05rem", color: white, lineHeight: 1.55, marginBottom: 22 }}>{q.q}</div>
          <div style={{ display: "grid", gap: 10 }}>
            {q.opts.map((opt, i) => {
              const isChosen = ans?.chosen === i, isCorrect = i === q.correct;
              let bg = navy3, bdr = border, col = textCol;
              if (ans) {
                if (isCorrect) { bg = "rgba(34,197,94,.08)"; bdr = green; col = "#4ade80"; }
                else if (isChosen) { bg = "rgba(239,68,68,.08)"; bdr = "#ef4444"; col = "#f87171"; }
              }
              return (
                <button key={i} onClick={() => answer(i)} disabled={!!ans}
                  style={{ background: bg, border: `1px solid ${bdr}`, borderRadius: 8, padding: "14px 18px", textAlign: "left", fontFamily: "inherit", fontSize: "0.88rem", color: col, cursor: ans ? "default" : "pointer", transition: "all 0.2s", display: "flex", alignItems: "center", gap: 12 }}>
                  <span style={{ width: 26, height: 26, flexShrink: 0, background: navy4, border: `1px solid ${border}`, borderRadius: 5, display: "inline-flex", alignItems: "center", justifyContent: "center", fontFamily: "monospace", fontSize: "0.72rem", color: muted }}>{["A","B","C","D"][i]}</span>
                  {opt}
                </button>
              );
            })}
          </div>
          {ans && (
            <>
              <div style={{ marginTop: 14, padding: "13px 16px", borderRadius: 7, fontSize: "0.84rem", lineHeight: 1.65, background: ans.correct ? "rgba(34,197,94,.07)" : "rgba(239,68,68,.07)", border: `1px solid ${ans.correct ? "rgba(34,197,94,.25)" : "rgba(239,68,68,.2)"}`, color: ans.correct ? "#86efac" : "#fca5a5" }}>
                {ans.correct ? "✓ " : "✗ "}{ans.correct ? q.fb.y : q.fb.n}
              </div>
              <button onClick={next} style={{ marginTop: 20, background: gold, color: navy, border: "none", padding: "12px 28px", fontFamily: "inherit", fontSize: "0.85rem", fontWeight: 700, borderRadius: 100, cursor: "pointer" }}>
                {current < total - 1 ? "Next Question →" : "See My Results →"}
              </button>
            </>
          )}
        </>
      ) : (
        <div style={{ textAlign: "center", padding: "20px 0" }}>
          <div style={{ width: 110, height: 110, borderRadius: "50%", margin: "0 auto 20px", background: `conic-gradient(${gold} 0% ${pct}%, ${navy4} ${pct}%)`, display: "flex", alignItems: "center", justifyContent: "center", position: "relative" }}>
            <div style={{ position: "absolute", inset: 14, borderRadius: "50%", background: navy2 }} />
            <span style={{ position: "relative", fontFamily: "Georgia,serif", fontSize: "1.5rem", fontWeight: 900, color: gold, zIndex: 1 }}>{score}/{total}</span>
          </div>
          <div style={{ fontFamily: "Georgia,serif", fontSize: "1.5rem", color: white, marginBottom: 8 }}>
            {score === total ? "Perfect Score! 🏆" : score >= 8 ? "Strong Result ✦" : score >= 6 ? "Good Effort" : "Keep Studying"}
          </div>
          <div style={{ fontSize: "0.9rem", color: muted, marginBottom: 24 }}>
            {score === total ? "Flawless. You've mastered Canadian taxation and retirement planning." : score >= 8 ? "Excellent. Review the questions you missed." : score >= 6 ? "Good foundation. Revisit the registered plans and tax calculations." : "Go back through the content — focus on RRSP limits, income types, and pension plan distinctions."}
          </div>
          <button onClick={retry} style={{ background: "transparent", color: gold, border: `1px solid rgba(245,166,35,.35)`, padding: "10px 26px", fontFamily: "inherit", fontSize: "0.85rem", fontWeight: 700, borderRadius: 100, cursor: "pointer" }}>↺ Try Again</button>
        </div>
      )}
    </div>
  );
}

// ── Glossary ──
const glossary = [
  { term: "Marginal Tax Rate", def: "The rate paid on the last dollar of income earned. Used to calculate the tax cost of adding new investment income." },
  { term: "Interest Income", def: "Investment income from GICs, bonds, and T-bills. 100% taxable at the marginal rate — most heavily taxed form." },
  { term: "Capital Gains", def: "Profit from selling an asset above its purchase price. Only 50% is included in taxable income (50% inclusion rate)." },
  { term: "Capital Gains Inclusion Rate", def: "The portion of a capital gain subject to tax. Currently 50% — meaning half the gain is added to taxable income." },
  { term: "Dividend Gross-Up", def: "Eligible Canadian dividends are grossed up by 38% to arrive at the 'taxable amount' before credits are applied." },
  { term: "Dividend Tax Credit (DTC)", def: "A federal credit equal to 15.02% of the grossed-up dividend, reducing tax payable to reflect corporate tax already paid." },
  { term: "CPP", def: "Canada Pension Plan — mandatory contribution plan. Available from 60 (reduced) to 70 (enhanced). Fully taxable." },
  { term: "OAS", def: "Old Age Security — based on residency, not work history. Subject to a clawback above ~$90,997 net income." },
  { term: "OAS Clawback", def: "If net income exceeds ~$90,997, 15 cents of OAS is repaid for every dollar above the threshold." },
  { term: "Defined Benefit Plan (DBP)", def: "Pension where the retirement benefit is known in advance; the employer bears the investment risk." },
  { term: "Defined Contribution Plan (DCP)", def: "Pension where contributions are fixed; the final benefit depends on investment returns. Employee bears the risk." },
  { term: "Final Average Plan", def: "DBP subtype using the best earnings years (e.g., best 3 of last 5) — best protection against inflation." },
  { term: "RRSP", def: "Registered Retirement Savings Plan. Contributions are tax-deductible; growth and withdrawals are taxed later." },
  { term: "Pension Adjustment (PA)", def: "Reduces RRSP room to account for benefits earned in an employer pension plan. Prevents double-sheltering." },
  { term: "Spousal RRSP", def: "A high-income earner contributes (and claims the deduction); withdrawals in retirement taxed in the spouse's hands." },
  { term: "RRIF", def: "Registered Retirement Income Fund. Converts an RRSP at age 71; mandatory minimum annual withdrawals apply." },
  { term: "TFSA", def: "Tax-Free Savings Account. After-tax contributions; growth and withdrawals are 100% tax-free." },
  { term: "RESP", def: "Registered Education Savings Plan. CESG government grant matches 20% of first $2,500/year (max $500/year)." },
  { term: "CESG", def: "Canada Education Savings Grant — 20% government match on first $2,500/year per child; lifetime max $7,200." },
];

// ── Main Export ──
export default function Chapter6() {
  const [progress, setProgress] = useState(0);
  useEffect(() => {
    const fn = () => {
      const h = document.documentElement;
      setProgress((h.scrollTop || document.body.scrollTop) / (h.scrollHeight - h.clientHeight) * 100);
    };
    window.addEventListener("scroll", fn);
    return () => window.removeEventListener("scroll", fn);
  }, []);

  const tocItems = [
    ["01", "Canadian Taxation — Income Types & Rates"],
    ["02", "Government Pension Plans (CPP & OAS)"],
    ["03", "Employer-Sponsored Pension Plans (RPPs)"],
    ["04", "Tax Deferral & Registered Plans"],
    ["🧮", "Tax Comparison & RRSP Calculators"],
    ["✦", "Knowledge Check Quiz"],
    ["✦", "Key Terms Glossary"],
  ];

  return (
    <div style={{ background: navy, minHeight: "100vh", fontFamily: "'Nunito Sans', sans-serif" }}>
      <div style={{ position: "fixed", top: 0, left: 0, height: 3, background: `linear-gradient(90deg,${gold},${blue})`, width: `${progress}%`, zIndex: 9999, transition: "width 0.15s ease" }} />
      <div style={{ maxWidth: 860, margin: "0 auto", padding: "0 24px 80px" }}>

        {/* HERO */}
        <Reveal>
          <div style={{ padding: "80px 0 56px", borderBottom: `1px solid ${border}`, marginBottom: 56 }}>
            <div style={{ display: "inline-flex", alignItems: "center", gap: 8, background: "rgba(245,166,35,.12)", border: "1px solid rgba(245,166,35,.25)", color: gold, fontSize: "0.72rem", fontWeight: 700, letterSpacing: "0.14em", textTransform: "uppercase", padding: "5px 14px", borderRadius: 100, marginBottom: 24 }}>
              <span style={{ width: 6, height: 6, borderRadius: "50%", background: gold, display: "inline-block" }} />
              IFC/CSC · Chapter 6
            </div>
            <h1 style={{ fontFamily: "Georgia,serif", fontSize: "clamp(2.2rem,5vw,3.4rem)", fontWeight: 900, color: white, lineHeight: 1.15, marginBottom: 20 }}>
              Taxation &amp; <span style={{ color: gold }}>Retirement Planning</span>
            </h1>
            <p style={{ fontSize: "1.05rem", color: muted, maxWidth: 600, lineHeight: 1.8, marginBottom: 32 }}>
              How different investment income types are taxed, Canada's government and employer pension systems, and the registered plans — RRSP, RRIF, TFSA, and RESP — that shape retirement and tax strategy.
            </p>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 24, paddingTop: 24, borderTop: `1px solid ${border}` }}>
              {[["📖","4","Core Topics"],["⏱","~20 min","read"],["🎯","10","practice questions"],["🧮","2","interactive calculators"]].map(([icon,val,lbl],i) => (
                <div key={i} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ color: blue }}>{icon}</span>
                  <span style={{ fontSize: "0.82rem", color: muted }}><strong style={{ color: textCol }}>{val}</strong> {lbl}</span>
                </div>
              ))}
            </div>
          </div>
        </Reveal>

        {/* TOC */}
        <Reveal delay={0.1}>
          <div style={{ background: navy3, border: `1px solid ${border}`, borderLeft: `3px solid ${gold}`, borderRadius: 8, padding: "28px 32px", marginBottom: 56 }}>
            <div style={{ fontFamily: "Georgia,serif", fontSize: "0.85rem", fontWeight: 700, color: gold, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 16 }}>📋 In This Chapter</div>
            <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
              {tocItems.map(([num, label], i) => (
                <li key={i} style={{ padding: "6px 0", borderBottom: i < tocItems.length - 1 ? `1px solid ${border}` : "none", display: "flex", alignItems: "center", gap: 12 }}>
                  <span style={{ fontFamily: "monospace", fontSize: "0.7rem", color: muted, width: 24, flexShrink: 0 }}>{num}</span>
                  <span style={{ color: textCol, fontSize: "0.9rem", fontWeight: 600 }}>{label}</span>
                </li>
              ))}
            </ul>
          </div>
        </Reveal>

        {/* ── S1: Taxation ── */}
        <div style={s.section}>
          <Reveal>
            <div style={s.eyebrow}><span style={s.tag}>Section 01</span><span style={s.badge}>Tax Fundamentals</span><div style={s.line} /></div>
            <h2 style={s.h2}>The Canadian Taxation System</h2>
            <p style={s.prose}>
              Taxes significantly impact real investment returns. The primary goal of tax planning is to <span style={{ color: gold, fontWeight: 600 }}>maximize after-tax income</span> — not simply minimize tax. Different forms of investment income receive very different tax treatment. Understanding these distinctions is critical to building tax-efficient portfolios.
            </p>
          </Reveal>
          <Reveal delay={0.1}><IncomeTypeTabs /></Reveal>
          <Reveal delay={0.2}>
            <Callout icon="📊">
              <strong>Tax Efficiency Ranking (from most to least taxed):</strong><br />
              <span style={{ color: red, fontWeight: 700 }}>1st — Interest Income</span>: 100% taxable. A 40% bracket investor keeps $0.60 per dollar.<br />
              <span style={{ color: gold, fontWeight: 700 }}>2nd — Canadian Dividends</span>: Grossed-up then credited. Effective rate is lower than interest.<br />
              <span style={{ color: green, fontWeight: 700 }}>3rd — Capital Gains</span>: Only 50% included. A 40% bracket investor pays just 20% effective tax on the gain.
            </Callout>
          </Reveal>
          <Reveal delay={0.3}><TaxCalculator /></Reveal>
        </div>

        <hr style={s.hr} />

        {/* ── S2: Government Pensions ── */}
        <div style={s.section}>
          <Reveal>
            <div style={s.eyebrow}><span style={s.tag}>Section 02</span><span style={s.badge}>Government Pensions</span><div style={s.line} /></div>
            <h2 style={s.h2}>Government Pension Plans</h2>
            <p style={s.prose}>
              Canada's two government pension programs provide a <span style={{ color: gold, fontWeight: 600 }}>base level of retirement income</span> — but they are rarely sufficient on their own for a comfortable retirement. Understanding the rules around each is critical both for the exam and for client planning.
            </p>
          </Reveal>
          <Reveal delay={0.1}><PensionCards /></Reveal>
          <Reveal delay={0.2}>
            <Callout icon="⏰" accent={blue}>
              <strong style={{ color: blue2 }}>CPP Timing Matters:</strong> Taking CPP at <strong style={{ color: blue2 }}>60</strong> gives you more years of payments but a permanently lower monthly amount. Taking it at <strong style={{ color: blue2 }}>70</strong> gives a much higher monthly amount but fewer years to collect. The break-even point is roughly age 74–75. Clients in poor health may benefit from taking it early; healthy clients with other income may benefit from deferring.
            </Callout>
          </Reveal>
        </div>

        <hr style={s.hr} />

        {/* ── S3: RPPs ── */}
        <div style={s.section}>
          <Reveal>
            <div style={s.eyebrow}><span style={s.tag}>Section 03</span><span style={s.badge}>Employer Pension Plans</span><div style={s.line} /></div>
            <h2 style={s.h2}>Employer-Sponsored Pension Plans (RPPs)</h2>
            <p style={s.prose}>
              <strong style={{ color: white }}>Registered Pension Plans (RPPs)</strong> are employer-sponsored trusts registered with the CRA. There are two fundamentally different structures — click each to explore their subtypes:
            </p>
          </Reveal>
          <Reveal delay={0.1}><RPPComparison /></Reveal>
          <Reveal delay={0.2}>
            <Callout icon="💡" accent={green}>
              <strong style={{ color: green }}>Key Exam Distinction:</strong> In a <strong style={{ color: green }}>DBP</strong>, the employer bears the investment risk — the employee knows exactly what they'll receive. In a <strong style={{ color: green }}>DCP</strong>, the employee bears the risk — contributions are known but retirement income is uncertain. The <strong style={{ color: green }}>Final Average Plan</strong> (DBP subtype) offers the best protection against inflation because it captures peak earning years.
            </Callout>
          </Reveal>
        </div>

        <hr style={s.hr} />

        {/* ── S4: Tax Deferral Plans ── */}
        <div style={s.section}>
          <Reveal>
            <div style={s.eyebrow}><span style={s.tag}>Section 04</span><span style={s.badge}>Registered Plans</span><div style={s.line} /></div>
            <h2 style={s.h2}>Tax Deferral &amp; Registered Plans</h2>
            <p style={s.prose}>
              Canada offers four major registered plans that either <span style={{ color: gold, fontWeight: 600 }}>defer tax</span> (RRSP, RRIF) or <span style={{ color: green, fontWeight: 600 }}>eliminate it entirely</span> (TFSA), plus a specialized vehicle for education savings (RESP). Click each tab to explore:
            </p>
          </Reveal>
          <Reveal delay={0.1}><TaxPlanTabs /></Reveal>
          <Reveal delay={0.2}>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(180px,1fr))", gap: 12, margin: "28px 0" }}>
              <FeatCard icon="🏦" title="RRSP → RRIF" body="Your RRSP must convert to a RRIF by end of the year you turn 71. Investments keep growing tax-sheltered, but mandatory minimum withdrawals begin." />
              <FeatCard icon="💸" title="TFSA Withdrawals" body="Any amount withdrawn from a TFSA is added back to your contribution room the following calendar year — room is never permanently lost." />
              <FeatCard icon="🎓" title="RESP & the CESG" body="The government's 20% CESG on the first $2,500/year makes RESPs incredibly powerful. Unused room carries forward — but annual CESG is capped at $500." />
              <FeatCard icon="⚖️" title="RRSP vs TFSA" body="RRSP beats TFSA when your tax rate is higher now than in retirement. TFSA beats RRSP when rates are equal or higher in retirement." />
            </div>
          </Reveal>
          <Reveal delay={0.3}><RRSPCalc /></Reveal>
        </div>

        <hr style={s.hr} />

        {/* ── Quiz ── */}
        <div style={s.section}>
          <Reveal>
            <div style={s.eyebrow}><span style={s.tag}>Knowledge Check</span><span style={s.badge}>10 Questions</span><div style={s.line} /></div>
            <h2 style={s.h2}>Test Your Understanding</h2>
            <p style={s.prose}>10 questions covering taxation, government pensions, RPPs, and registered plans from Chapter 6. Detailed feedback on every answer.</p>
          </Reveal>
          <Reveal delay={0.1}><Quiz /></Reveal>
        </div>

        <hr style={s.hr} />

        {/* ── Glossary ── */}
        <div style={s.section}>
          <Reveal>
            <div style={s.eyebrow}><span style={s.tag}>Quick Reference</span><span style={s.badge}>Key Terms</span><div style={s.line} /></div>
            <h2 style={s.h2}>Glossary</h2>
            <p style={s.prose}>All essential terms from Chapter 6 — taxation, pensions, and registered plans.</p>
          </Reveal>
          <Reveal delay={0.1}>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(240px,1fr))", gap: 10, margin: "28px 0" }}>
              {glossary.map((g, i) => (
                <div key={i} style={{ background: navy3, border: `1px solid ${border}`, borderRadius: 8, padding: "18px 20px", transition: "border-color 0.2s", cursor: "default" }}
                  onMouseEnter={e => e.currentTarget.style.borderColor = "rgba(245,166,35,.3)"}
                  onMouseLeave={e => e.currentTarget.style.borderColor = border}>
                  <div style={{ fontFamily: "monospace", fontSize: "0.72rem", color: gold, letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: 6 }}>{g.term}</div>
                  <div style={{ fontSize: "0.83rem", color: muted, lineHeight: 1.65 }}>{g.def}</div>
                </div>
              ))}
            </div>
          </Reveal>
        </div>

      </div>
    </div>
  );
}
